import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CardModule, GridModule, ButtonModule, SpinnerModule, BadgeModule, AlertModule, ModalModule, ButtonCloseDirective } from '@coreui/angular-pro';
import { TranslatePipe, TranslationModule } from 'i18n';
import { Logger } from 'shared';

// Weekly settlement history + manual trigger (v4.0 ch.6/7, 30-33).
//
// The page exists to make the WATERFALL visible: fixed bonuses are served before
// the points, so a point value of zero is a legitimate outcome. Without showing
// where the money went first, a zero week is indistinguishable from a broken run —
// which is exactly why the run is triggered by hand for now.
@Component({
  selector: 'app-wpcp-pool-page',
  standalone: true,
  imports: [CommonModule, FormsModule, CardModule, GridModule, ButtonModule, SpinnerModule, BadgeModule, AlertModule, ModalModule, ButtonCloseDirective, TranslationModule, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './wpcp-pool-page.component.html'
})
export class WpcpPoolPageComponent {
  private http = inject(HttpClient);
  private destroyRef = inject(DestroyRef);
  private logger = new Logger('WpcpPoolPageComponent');

  config = signal<any | null>(null);
  settlements = signal<any[]>([]);
  loading = signal(false);
  message = signal<{ type: string; text: string } | null>(null);

  preview = signal<any | null>(null);
  detail = signal<any | null>(null);
  detailVisible = signal(false);

  weekKey = '';
  /** Detail modal: show one component at a time, or all of them. */
  kindFilter = '';

  constructor() {
    this.loadConfig();
    this.loadSettlements();
  }

  private loadConfig() {
    this.http.get<any>('/wpcp/admin/pool/config').pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (c) => this.config.set(c),
      error: (e) => this.logger.warn('pool config failed', e)
    });
  }

  loadSettlements() {
    this.loading.set(true);
    this.http.get<any>('/wpcp/admin/pool/settlements').pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (r) => { this.settlements.set(r.data || []); this.loading.set(false); },
      error: (e) => { this.logger.error('settlements failed', e); this.loading.set(false); }
    });
  }

  runDry() {
    const body: any = { dry_run: true };
    if (this.weekKey.trim()) body.settlement_key = this.weekKey.trim();
    this.http.post<any>('/wpcp/admin/pool/settlements', body).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (r) => this.preview.set(r),
      error: (e) => this.flash('danger', e?.error?.error || 'Preview failed')
    });
  }

  runReal() {
    const body: any = {};
    if (this.weekKey.trim()) body.settlement_key = this.weekKey.trim();
    this.http.post<any>('/wpcp/admin/pool/settlements', body).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => { this.flash('success', 'Settlement enqueued'); setTimeout(() => this.loadSettlements(), 1500); },
      error: (e) => this.flash('danger', e?.error?.error || 'Run failed')
    });
  }

  openDetail(s: any) {
    this.http.get<any>(`/wpcp/admin/pool/settlements/${s.id}`).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (r) => { this.detail.set(r); this.detailVisible.set(true); },
      error: (e) => this.logger.error('detail failed', e)
    });
  }

  currencyKeys(snapshot: any): string[] {
    return snapshot ? Object.keys(snapshot) : [];
  }

  /** Fixed bonuses needed more than the compensation side alone could cover. */
  usedLegacy(v: { legacy_transfer?: any } | null | undefined): boolean {
    return Number(v?.legacy_transfer ?? 0) > 0;
  }

  /** Not even both sides together covered the fixed bonuses — everyone was cut. */
  isCut(v: { funding_ratio?: any } | null | undefined): boolean {
    return Number(v?.funding_ratio ?? 1) < 1;
  }

  ratioPct(ratio: any): number {
    return Number(ratio ?? 1) * 100;
  }

  // --- detail modal: per-component totals -------------------------------
  // A completed run has one line per Legacy Note holder — several hundred of them.
  // Without totals per component the flat list hides what each leg actually paid.

  kindsIn(payouts: any[] | null | undefined): string[] {
    return Array.from(new Set((payouts || []).map((p) => p.kind))).sort();
  }

  countOfKind(payouts: any[] | null | undefined, kind: string): number {
    return (payouts || []).filter((p) => p.kind === kind).length;
  }

  sumOfKind(payouts: any[] | null | undefined, kind: string): number {
    return (payouts || [])
      .filter((p) => p.kind === kind && p.status === 'credited')
      .reduce((acc, p) => acc + Number(p.final_credit ?? 0), 0);
  }

  filteredPayouts(payouts: any[] | null | undefined): any[] {
    const all = payouts || [];
    return this.kindFilter ? all.filter((p) => p.kind === this.kindFilter) : all;
  }

  statusColor(status: string): string {
    switch (status) {
      case 'completed': return 'success';
      case 'failed': return 'danger';
      case 'manual_review': return 'warning';
      default: return 'secondary';
    }
  }

  private flash(type: string, text: string) {
    this.message.set({ type, text });
    setTimeout(() => this.message.set(null), 4000);
  }
}
